import React from 'react';
import {
  useTranslation,
  formatDate as i18nFormatDate,
  formatNumber as i18nFormatNumber,
  getCurrentLocale
} from '../../../../shared/i18n';
import { useDashboard } from '../../hooks/useDashboard';
import { FamilyInfo } from '../FamilyInfo';
import { Children } from '../Children';
import { QuickStats } from '../QuickStats';
import { ReferralProgram } from '../ReferralProgram';
import { FamilySafety } from '../FamilySafety';
import { NeedHelp } from '../NeedHelp';

/**
 * Dashboard - Main dashboard content component for guardian
 * Fetches data from API and renders dashboard sections
 */
export const Dashboard = () => {
  const { data, isLoading, error } = useDashboard();
  const { t } = useTranslation();

  // Helper function to format date using locale-aware formatting
  const formatDate = (dateString, format = 'long') => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const locale = getCurrentLocale() === 'vi' ? 'vi-VN' : 'en-US';

    if (format === 'short') {
      // Short format: "06 Thg 12" (2-digit day + abbreviated month)
      const formatted = new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short' }).format(date);
      if (locale === 'vi-VN') {
        return formatted.replace(/\bthg\b/, 'Thg');
      }
      return formatted;
    } else {
      // Long format: "YYYY/MM/DD" for clear, unambiguous date display
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}/${month}/${day}`;
    }
  };

  // Helper function to format numbers using locale-aware formatting
  const formatNumber = (num) => {
    return i18nFormatNumber(num);
  };

  if (isLoading) {
    return (
      <div className="bg-light">
        <div className="container py-4">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-light">
        <div className="container py-4">
          <div className="alert alert-danger">
            {t('common.error')}: {error.message}
          </div>
        </div>
      </div>
    );
  }

  const { user, family, children, stats, trial_settings, support } = data;

  return (
    <div className="guardian-dashboard">
      <div className="bg-light">
        <div className="container py-4">
          {/* Page Header */}
          <div className="mb-4">
            <h2 className="h1 fw-bold mb-2">{t('dashboard.index.title')}</h2>
            <p className="text-muted">{t('dashboard.index.description')}</p>
          </div>

          <div className="row g-4">
            {/* Main Column */}
            <div className="col-lg-8">
              <FamilyInfo family={family} t={t} />
              <Children children={children} family={family} t={t} formatNumber={formatNumber} formatDate={formatDate} />
              <NeedHelp t={t} support={support} />
            </div>

            {/* Sidebar Column */}
            <div className="col-lg-4">
              <QuickStats stats={stats} t={t} />
              <ReferralProgram user={user} t={t} trialSettings={trial_settings} referralBonusPercentage={user.referral_bonus_percentage} />
              <FamilySafety t={t} />
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Dashboard;
